import { useCallback, useEffect, useState } from "react";
import { Check, ExternalLink, Trash2 } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { log } from "../../lib/log";
import { Card } from "./_shared";

// ---------------------------------------------------------------------------
// Reports Tab — list / resolve / delete user-submitted reports
// ---------------------------------------------------------------------------
export const CATEGORY_LABEL = {
  wrong_method: "Wrong method shown",
  missing_method: "Missing method",
  wrong_bonus: "Bonus info wrong",
  false_positive: "Wrongly verified",
  other: "Other",
};

export function ReportsTab({ onChange }) {
  const { apiFetch } = useAuth();
  const [filter, setFilter] = useState("open");
  const [reports, setReports] = useState(null);
  const [counts, setCounts] = useState({ open: 0, total: 0 });
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setReports(null);
    try {
      const path = `/api/reports${filter === "all" ? "" : `?status=${filter}`}`;
      const d = await apiFetch(path);
      setReports(d.items || []);
      setCounts({ open: d.open || 0, total: d.total || 0 });
    } catch { setReports([]); }
  }, [apiFetch, filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await apiFetch(`/api/reports/${encodeURIComponent(id)}/status`, {
        method: "POST",
        body: JSON.stringify({ status }),
      });
      load(); onChange?.();
    } catch (e) { log.warn("[admin] report update failed:", e?.message); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };
  const removeReport = async (id) => {
    if (!confirm("Delete this report?")) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await apiFetch(`/api/reports/${encodeURIComponent(id)}`, { method: "DELETE" });
      load(); onChange?.();
    } catch (e) { log.warn("[admin] report delete failed:", e?.message); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  return (
    <div className="space-y-4">
      <Card title="USER REPORTS"
            subtitle={`${counts.open} open · ${counts.total} total. No AI action is taken — review and decide manually.`}
            accent="#F59E0B" testid="reports-card">
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="reports-filter">
          {[
            { k: "open", label: "Open" },
            { k: "resolved", label: "Resolved" },
            { k: "all", label: "All" },
          ].map((o) => (
            <button key={o.k} onClick={() => setFilter(o.k)}
              data-testid={`reports-filter-${o.k}`}
              className={`px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border transition-colors ${filter === o.k ? "text-black bg-[#F59E0B] border-[#F59E0B]" : "text-[var(--srs-ink-dim)] border-[var(--srs-line)] hover:text-[var(--srs-ink)]"}`}>
              {o.label}
            </button>
          ))}
          <button onClick={load} className="ml-auto px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] transition-colors" data-testid="reports-refresh">
            Refresh
          </button>
        </div>

        {reports === null && <div className="text-xs text-[var(--srs-ink-faint)]">Loading…</div>}
        {reports?.length === 0 && (
          <div className="text-xs text-[var(--srs-ink-faint)] py-8 text-center" data-testid="reports-empty">
            No reports {filter === "open" ? "open right now" : "found"}.
          </div>
        )}

        <div className="flex flex-col gap-3" data-testid="reports-list">
          {reports?.map((r) => (
            <div key={r.id} className="border border-[var(--srs-line)] bg-[var(--srs-surface-2)] p-4" data-testid={`report-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-[var(--srs-ink)] truncate">{r.host}</span>
                    <span
                      className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase"
                      style={{
                        background: r.status === "open" ? "rgba(245,158,11,0.12)" : "rgba(16,185,129,0.12)",
                        color: r.status === "open" ? "#F59E0B" : "#10B981",
                        border: `1px solid ${r.status === "open" ? "rgba(245,158,11,0.35)" : "rgba(16,185,129,0.35)"}`,
                      }}>
                      {r.status}
                    </span>
                    <span className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase bg-[var(--srs-surface-2)] text-[var(--srs-ink-dim)] border border-[var(--srs-line)]">
                      {CATEGORY_LABEL[r.category] || r.category}
                    </span>
                  </div>
                  <div className="text-[10px] text-[var(--srs-ink-faint)] mt-1 tracking-wide">
                    {new Date(r.created_at).toLocaleString()} {r.url && (
                      <a href={r.url} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)]">
                        <ExternalLink className="w-3 h-3" /> open
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {r.status === "open" ? (
                    <button onClick={() => setStatus(r.id, "resolved")} disabled={busy[r.id]}
                      data-testid={`report-resolve-${r.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase bg-[#10B981] hover:bg-[#0e9e6e] disabled:bg-[var(--srs-line-soft)] text-black transition-colors">
                      <Check className="w-3 h-3" /> Resolve
                    </button>
                  ) : (
                    <button onClick={() => setStatus(r.id, "open")} disabled={busy[r.id]}
                      data-testid={`report-reopen-${r.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] hover:border-[#F59E0B] hover:text-[#F59E0B] text-[var(--srs-ink-dim)] transition-colors">
                      Reopen
                    </button>
                  )}
                  <button onClick={() => removeReport(r.id)} disabled={busy[r.id]}
                    data-testid={`report-delete-${r.id}`}
                    className="inline-flex items-center justify-center w-8 h-8 border border-[var(--srs-line)] hover:border-[#EF4444] hover:text-[#EF4444] text-[var(--srs-ink-dim)] transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-[var(--srs-ink)] leading-relaxed whitespace-pre-wrap break-words">{r.message}</p>
              {(r.detected_methods?.length > 0 || r.page_title) && (
                <div className="mt-3 pt-3 border-t border-[var(--srs-line-soft)] flex flex-wrap gap-3 text-[10px] text-[var(--srs-ink-faint)] tracking-wide">
                  {r.page_title && <span className="truncate max-w-[60%]">Title: <span className="text-[var(--srs-ink-dim)]">{r.page_title}</span></span>}
                  {r.detected_methods?.length > 0 && (
                    <span>Detected: <span className="text-[var(--srs-ink-dim)] font-mono">{r.detected_methods.join(", ")}</span></span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
